import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "../styles/CreateService.css";
import { graphqlRequest } from "../../../utils/graphqlRequest";

function suggestCode(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word.slice(0, 3).toUpperCase())
    .join("-");
}

export default function CreateService({ isOpen, onClose, onSave }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setCode("");
    setCodeTouched(false);
    setCategory("");
    setDescription("");
    setPreviewImage(null);
    setActive(true);
  }, [isOpen]);

  if (!isOpen) return null;

  const readFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const serviceName = name.trim();
    const serviceCode = (code || suggestCode(serviceName)).trim();
    if (!serviceName || !serviceCode) {
      toast.error("Service name is required");
      return;
    }

    try {
      setLoading(true);
      const created = await graphqlRequest(
        `mutation CreateService($input: CreateService!, $fileKey: String) {
          createServices(CreateService: $input, fileKeys: $fileKey) {
            id
            service
            description
            serviceId
          }
        }`,
        {
          input: {
            service: serviceName,
            description: description.trim() || " ",
            serviceId: serviceCode,
          },
          fileKey: previewImage || null,
        },
      );
      const row = created.createServices;
      if (category.trim() || !active) {
        await graphqlRequest(
          `mutation FinishNewService($updateService: UpdateService!) {
            updateService(updateService: $updateService) { id status category }
          }`,
          {
            updateService: {
              id: row.id,
              service: serviceName,
              description: description.trim() || " ",
              serviceId: serviceCode,
              status: active ? "ACTIVE" : "INACTIVE",
              category: category.trim() || null,
            },
          },
        );
      }
      toast.success("Service created");
      onSave?.({
        id: row.id,
        name: row.service,
        description: description.trim(),
        code: row.serviceId,
        category: category.trim(),
        active,
        icon: previewImage,
      });
      onClose();
    } catch (error) {
      toast.error(error.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-services-overlay">
      <form className="svc-create" onSubmit={handleSubmit}>
        <p className="svc-create-step"><i>1</i> Basics</p>
        <h2>Name your service</h2>
        <p className="svc-create-lead">Set the basic details — you can edit everything later.</p>

        <label>
          Service Name *
          <input
            value={name}
            placeholder="e.g. Residential Cleaning"
            onChange={(e) => {
              setName(e.target.value);
              if (!codeTouched) setCode(suggestCode(e.target.value));
            }}
            required
          />
        </label>
        <label>
          Service Code
          <input
            value={code}
            placeholder="Auto-suggested from name"
            onChange={(e) => {
              setCodeTouched(true);
              setCode(e.target.value);
            }}
          />
          <small>Must be unique across your branch. Used in quotations and reports.</small>
        </label>
        <label>
          Category
          <input
            value={category}
            placeholder="e.g. Residential, Cleaning, End of lease"
            onChange={(e) => setCategory(e.target.value)}
          />
        </label>
        <label>
          Description
          <textarea
            maxLength={200}
            rows={3}
            placeholder="One sentence describing what the service covers..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <small className="svc-create-count">{description.length}/200</small>
        </label>
        <span>Add Cover</span>
        <label
          className="svc-create-cover"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            readFile(e.dataTransfer.files?.[0]);
          }}
        >
          <input
            type="file"
            accept=".png,.jpg,.jpeg"
            hidden
            onChange={(e) => readFile(e.target.files?.[0])}
          />
          {previewImage ? (
            <img src={previewImage} alt="" />
          ) : (
            <>
              <b>↑</b>
              <span>Click to browse or drag and drop</span>
            </>
          )}
        </label>
        <div className="svc-create-active">
          <span>
            <strong>Active immediately</strong>
            <small>Visible to quotations right away</small>
          </span>
          <button
            type="button"
            className={active ? "is-on" : ""}
            aria-pressed={active}
            onClick={() => setActive((on) => !on)}
          />
        </div>
        <div className="svc-create-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={loading}>{loading ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </div>
  );
}
